/**
 * WHERE OUR OWN OVERPASS BOX CAN ANSWER — and what to do where it cannot.
 *
 * The private box at OVERPASS_ENDPOINT holds one Geofabrik extract:
 * `us-northeast`. Everywhere else it answers HTTP 200 with zero elements,
 * fast and confidently, which is why the edge function refuses to consult it
 * outside a declared rectangle (PRIVATE_BBOX in supabase/functions/overpass/
 * index.ts). Coverage is DECLARED, not inferred.
 *
 * THE CLIENT DID NOT KNOW ANY OF THIS. That is the bug this file exists for.
 *
 * Measured Aug 31, Morgan's phone, a search near Waco (31.5N, -97.2W):
 *
 *     four searches, 25332 / 25074 / 25330 / 25158 ms, all deep=0/N
 *     27, 33, 80 combined cells and 8 scenic cells, served=0 on every one
 *
 * The chain: Texas is outside PRIVATE_BBOX, so the edge function correctly
 * drops the fast box and is left with the public mirrors -- which throttle
 * Supabase's cloud IP, which is the entire reason the private box exists. It
 * then grinds to its own REQUEST_DEADLINE_MS of 20s and returns nothing. And
 * `fetchOneTileCell` had NO client-side timeout, so every cell waited out that
 * full 20s. In coverage this never showed, because the box answers in ~1s.
 *
 * The Aug 31 corridor tiling made it worse in exactly this case: one scenic
 * request became eight cells, so out of coverage it became eight 20s waits at
 * TILE_FETCH_CONCURRENCY 4. In coverage that same change is a 4.6x win. Both
 * are true; this file is what makes the second one not cost the first.
 *
 * WHAT THIS IS NOT. It is not a decision that ground outside the rectangle has
 * no scenery. The public mirrors do hold Texas, and when they answer inside the
 * budget we use what they send. It is a decision about how long we are willing
 * to WAIT to find out -- after which the honest answer is UNMEASURED (rule 20),
 * reached in 8 seconds instead of 25.
 *
 * DO NOT WIDEN THE RECTANGLE. It already over-claims, and this gate inherits
 * that: inside the box but outside the extract, the private Overpass returns a
 * confident zero and we will (wrongly) call the ground covered. Measured
 * against the corners, the claimed-but-unheld ground is Maryland, Delaware,
 * DC, northern Virginia, northern West Virginia -- and, by far the largest,
 * southern Ontario and southern Quebec. Toronto sits comfortably inside this
 * rectangle, which is how the Aug 30 Toronto->Maine corridor reported coverage
 * 0.945 against a true 0.655.
 *
 * The west edge is tighter than it looks: -80.7 clips the WV panhandle, so
 * Wheeling is outside and Morgantown is inside. Both are pinned in
 * overpassCoverage.test.ts, because I guessed that boundary wrong once.
 *
 * None of this is made worse by this file -- it is today's behaviour, now
 * written down. The fix is a real extract-shaped polygon, the way
 * scenic_features already does it, not a bigger box. See
 * claude/Route_Data_Sources_Audit_Aug30.md.
 */

export interface CoverageTile {
  south: number;
  west: number;
  north: number;
  east: number;
}

/**
 * MUST match PRIVATE_BBOX in supabase/functions/overpass/index.ts.
 *
 * The edge function cannot import from src/ (separate Deno deploy) and this
 * module cannot import from supabase/functions/, so the constant is duplicated
 * on purpose. `overpassCoverage.drift.test.ts` reads the edge function's source
 * and fails if the two ever disagree -- a comment would not have.
 */
export const PRIVATE_COVERAGE = {
  south: 38.7,
  north: 47.6,
  west: -80.7,
  east: -66.5,
} as const;

/**
 * GROUND INSIDE THE RECTANGLE THAT THE EXTRACT DOES NOT HOLD.
 *
 * MUST match NOT_IN_EXTRACT in supabase/functions/overpass/index.ts;
 * overpassCoverage.test.ts reads that file and fails if they drift.
 *
 * The rectangle above claims a large piece of Canada, and the extract is
 * `us-northeast`. Measured Sep 1 against the box with fresh bounding boxes
 * (a repeated query is answered `duplicate_query`, so a re-probe reads as
 * empty and would have confirmed anything):
 *
 *     Danbury CT      403 ways        Toronto ON        0 ways
 *     Marlborough MA  2000 (capped)   Montreal QC       0 ways
 *     Lewiston ME     2000 (capped)
 *     Buffalo NY      2000 (capped)
 *
 * Cost of getting this wrong is ASYMMETRIC, which is why the boxes below are
 * timid rather than tight:
 *
 *   - Wrongly calling Canadian ground COVERED costs a full-price wait for
 *     nothing. Inside the rectangle there is deliberately no client deadline
 *     (the Aug 9 lesson), so a Toronto search pays ~25s and gets zero. Worse,
 *     the edge function keeps the private box at the head of the mirror list
 *     and its confident empty answer ENDS the chain -- so the public mirrors,
 *     which do hold Toronto, are never asked. Measured Sep 1: corridor
 *     Toronto->Guelph served 0 of 52 cells.
 *   - Wrongly calling US ground UNCOVERED drops our own fast box from that
 *     chain and sends a query the mirrors will throttle.
 *
 * Both are bad, so every rectangle below keeps a wide margin from the nearest
 * US land and the list is deliberately INCOMPLETE. Hamilton, St Catharines and
 * the Quebec strip north of western Maine are still wrongly claimed: no
 * axis-aligned box separates Hamilton (43.26N) from Youngstown NY (43.25N).
 * This narrows a known over-claim; it does not close it.
 *
 * THE REAL FIX IS STILL A POLYGON, the way scenic_features already does it.
 * See claude/Duplicate_Query_And_Canada_Box_Sep01.md.
 */
export const NOT_IN_EXTRACT: readonly CoverageTile[] = [
  // Ontario north and west of Lake Ontario: Toronto, Mississauga, Barrie,
  // Peterborough. Nearest US land is Fair Haven NY at 43.33N, ~30 km south.
  { south: 43.60, north: 47.60, west: -80.70, east: -76.90 },
  // Eastern Ontario and southern Quebec: Ottawa, Montreal, Trois-Rivieres,
  // Sherbrooke. The border here IS the 45th parallel, so 45.10 clears it --
  // including the Fort Montgomery anomaly at 45.016N.
  { south: 45.10, north: 47.60, west: -76.90, east: -71.60 },
  // Quebec City and Thetford Mines. East edge stops at -71.10 because Maine
  // begins at about -71.08; New Hampshire's northern tip is 45.305N.
  { south: 45.35, north: 47.60, west: -71.60, east: -71.10 },
  // New Brunswick: Fredericton and north. Calais ME is at -67.28 and Lubec ME,
  // the easternmost US land, is at 44.81N -- both clear of this box.
  { south: 45.30, north: 47.60, west: -67.00, east: -66.50 },
] as const;

/** True when the tile overlaps ground the extract is known not to hold, even
 *  partly. Partial overlap counts, for the same reason partial overlap of the
 *  outer rectangle counts: half an answer scored as a whole one is the
 *  measurement-of-absence defect this codebase keeps finding. */
export function tileOutsideExtract(t: CoverageTile): boolean {
  return NOT_IN_EXTRACT.some((x) =>
    t.west < x.east && t.east > x.west && t.south < x.north && t.north > x.south);
}

/**
 * True when the whole tile sits inside the declared extract. Partial overlap
 * counts as OUTSIDE, matching `tileInPrivateCoverage` in the edge function:
 * a cell straddling the boundary is served by the box for one half and by
 * nothing for the other, and a half-answer scored as a whole one is the same
 * measurement-of-absence defect this codebase keeps finding.
 */
export function tileInPrivateCoverage(t: CoverageTile): boolean {
  return t.south >= PRIVATE_COVERAGE.south && t.north <= PRIVATE_COVERAGE.north
      && t.west  >= PRIVATE_COVERAGE.west  && t.east  <= PRIVATE_COVERAGE.east
      && !tileOutsideExtract(t);
}

/**
 * How long ground outside the extract gets before we call it unmeasured.
 *
 * 8s is the edge function's own MIRROR_TIMEOUT_MS -- one honest attempt at a
 * public mirror. A mirror that has not answered in 8s is throttling us, and
 * waiting the remaining 12s of the edge function's deadline has never once
 * turned that into data. Applied per cell AND as a budget across the whole
 * tile set, so eight cells cannot serially become eight budgets.
 */
export const OUT_OF_COVERAGE_BUDGET_MS = 8_000;
